"use client";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";

const getViewStateTitle = (viewstate: string): string => {
  const viewStateMap: { [key: string]: string } = {
    // Patient vitals
    heart_rate: "Heart Rate",
    bp: "Blood Pressure",
    temperature: "Temperature",
    respiratory_rate: "Respiratory Rate",
    oxygen_saturation: "Oxygen Saturation",

    // Activity journals
    exercise: "Activity journal",
    diet: "Activity journal",
    sleep: "Activity journal",
    medication: "Activity journal",
    moods: "Activity journal",
    fluid: "Activity journal",

    // Patient data
    weight: "Weight",
    glucose: "Glucose",
    lab_test: "Lab Test",
    abnormality_report: "Abnormality Report",
    clinical_documents: "Clinical Documents",
    insulin_prescription: "Insulin Prescription",
    health_profile: "Health Profile",
    self_notes: "Self Notes",
    insights: "Summary",
    follow_up: "Follow Up",
    chat: "Chat",

    // Dashboard views
    onboard_pending: "Onboard Pending",
    new_patients: "New Patients",
    matrix_report: "Matrix Report",
    active_patients: "Active Patients",
    inactive_patients: "Inactive Patients",
    link_patient: "Link Patient",

    // Clinical notes types
    clinical: "Clinical Notes",
    special: "Special Notes",
    investigation: "Investigation Notes",
    diagnosis: "Diagnosis Notes",
    recommendation: "Recommendation Notes",
  };
  return (
    viewStateMap[viewstate] ||
    viewstate.charAt(0).toUpperCase() + viewstate.slice(1).replace(/_/g, " ")
  );
};

const getPageTitle = (
  pathname: string,
  searchParams: URLSearchParams
): string => {
  const SUFFIX = "WeCare";
  const viewstate = searchParams.get("viewstate");

  // Valid routes in the application
  const validRoutes = [
    "/",
    "/login",
    "/dashboard",
    "/patient",
    "/patients",
    "/doctors",
    "/coaches",
    "/clinics",
    "/diet-plans",
    "/linking-request",
    "/country-admin",
    "/profile",
    "/forgot-password",
    "/updates",
  ];

  // Check for invalid URLs
  if (!validRoutes.includes(pathname)) {
    return `404: Not found - ${SUFFIX}`;
  }

  if (viewstate) {
    return `${getViewStateTitle(viewstate)} - ${SUFFIX}`;
  }

  // Page name mapping for proper titles
  const pageNameMap: { [key: string]: string } = {
    "linking-request": "Linking requests",
    "diet-plans": "Diet plans",
    "country-admin": "Country admin",
    "forgot-password": "Forgot password",
    dashboard: "Dashboard",
    patient: "Patients",
    patients: "Patients",
    doctors: "Doctors",
    coaches: "Coaches",
    clinics: "Clinics",
    diet: "Diet",
    updates: "Updates",
    profile: "Profile",
    login: "Sign in",
  };

  const pathSegments = pathname.split("/").filter(Boolean);
  const pageName = pathSegments[pathSegments.length - 1] || "Sign in";
  const displayName =
    pageNameMap[pageName] ||
    pageName.charAt(0).toUpperCase() + pageName.slice(1);
  return `${displayName} - ${SUFFIX}`;
};

export default function DynamicTitle() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    document.title = getPageTitle(pathname, searchParams);
  }, [pathname, searchParams]);

  return null;
}
